import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { formatMoney } from '@/domain/store-setup/money';
import {
  activeSizes,
  hasSizes,
  ICatalogFilterContext,
  matchesCatalogFilter,
  sortedSizes,
  TCatalogFilter,
} from './catalog-filters';
import {
  availabilityStatus,
  isAvailableHere,
  TAvailabilityStatus,
} from './availability';

/**
 * The Menú page's derived view (plan §5.6): the current menu's categories
 * with their products, and one frozen view object per row, cached per item
 * and category object so an upsert re-renders only the rows it replaced.
 */
export interface ICatalogSection {
  category: ICategory;
  items: IItem[];
}

export function menuIdOf(entity: { menuId?: number | null }): number | null {
  return entity.menuId ?? null;
}

function byIndexThenId(
  a: { index?: number; id: number },
  b: { index?: number; id: number }
): number {
  return (a.index ?? 0) - (b.index ?? 0) || a.id - b.id;
}

/** The menu's live categories in order, each with its products in order. */
export function groupCatalog(
  categories: readonly ICategory[] | null,
  items: readonly IItem[] | null,
  menuId: number | null
): ICatalogSection[] {
  const sections = (categories ?? [])
    .filter((category) => menuIdOf(category) === menuId)
    .sort(byIndexThenId)
    .map((category) => ({ category, items: [] as IItem[] }));
  const byCategory = new Map(
    sections.map((section) => [section.category.id, section])
  );
  for (const item of items ?? [])
    byCategory.get(item.categoryId)?.items.push(item);
  for (const section of sections) section.items.sort(byIndexThenId);
  return sections;
}

/**
 * The new sections, keeping each previous section object whose category
 * object, products (by identity) and counts are unchanged: its card then
 * gets the same prop and does not re-render.
 */
export function shareSections<S extends ICatalogSection>(
  next: readonly S[],
  previous: readonly S[]
): S[] {
  const before = new Map(
    previous.map((section) => [section.category.id, section])
  );
  return next.map((section) => {
    const old = before.get(section.category.id);
    return old && sameSection(old, section) ? old : section;
  });
}

function sectionCounts(section: ICatalogSection): number[] {
  return 'matchCount' in section && 'total' in section
    ? [Number(section.matchCount), Number(section.total)]
    : [];
}

function sameSection(a: ICatalogSection, b: ICatalogSection): boolean {
  return (
    a.category === b.category &&
    a.items.length === b.items.length &&
    a.items.every((item, index) => item === b.items[index]) &&
    sectionCounts(a).join() === sectionCounts(b).join()
  );
}

export interface IShownSection extends ICatalogSection {
  /** Products matching the filter or search (pinned rows excluded). */
  matchCount: number;
  /** Every product of the category. */
  total: number;
}

export interface IFilterSectionsOptions {
  filter: TCatalogFilter;
  /** The search matcher; null without a search. */
  matcher: ((item: IItem) => boolean) | null;
  /** Rows acted on stay while they no longer match. */
  pinnedIds: Readonly<Record<number, true>>;
  ctx: ICatalogFilterContext;
}

/** Sections under the active filter and search; categories without rows drop out. */
export function filterSections(
  sections: readonly ICatalogSection[],
  options: IFilterSectionsOptions
): IShownSection[] {
  const { filter, matcher, pinnedIds, ctx } = options;
  if (filter === 'all' && !matcher)
    return sections.map((section) => ({
      ...section,
      matchCount: section.items.length,
      total: section.items.length,
    }));

  const shown: IShownSection[] = [];
  for (const section of sections) {
    let matchCount = 0;
    const items = section.items.filter((item) => {
      const matches =
        matchesCatalogFilter(filter, item, section.category, ctx) &&
        (!matcher || matcher(item));
      if (matches) matchCount++;
      return matches || !!pinnedIds[item.id];
    });
    if (items.length)
      shown.push({
        category: section.category,
        items,
        matchCount,
        total: section.items.length,
      });
  }
  return shown;
}

//#region Row view

export type TPriceTone = 'normal' | 'missing' | 'missingNeutral';

export interface ICatalogPriceView {
  /** «$ 12.500», or the lowest size price; empty when there is no price. */
  text: string;
  /** The highest size price when the sizes differ («{text} a {highText}»). */
  highText: string | null;
  /** The struck-through price before the discount (single prices only). */
  oldText: string | null;
  tone: TPriceTone;
}

export interface ICatalogRowView {
  id: number;
  categoryId: number;
  name: string;
  description: string | null;
  picture: string | null;
  price: ICatalogPriceView;
  /** Alive sizes; 0 for a single price. */
  sizesCount: number;
  /** Active option groups linked to the product. */
  groupsCount: number;
  /** The switch: can be ordered and sold here. */
  available: boolean;
  status: TAvailabilityStatus | null;
  /** The product or its category has a schedule. */
  hasHours: boolean;
}

export interface ICatalogRowContext {
  branchId: number | null;
  currency: string;
  country: string;
}

/** `items/abc.jpg` → `items/abc_thumb.jpg`, the list-size copy the uploader writes. */
export function thumbPath(picture: string): string {
  const dot = picture.lastIndexOf('.');
  const slash = picture.lastIndexOf('/');
  return dot > slash
    ? `${picture.slice(0, dot)}_thumb${picture.slice(dot)}`
    : `${picture}_thumb`;
}

function priceView(
  item: IItem,
  category: ICategory,
  ctx: ICatalogRowContext
): ICatalogPriceView {
  const money = (value: number) =>
    formatMoney(value, ctx.currency, ctx.country);
  const missing: ICatalogPriceView = {
    text: '',
    highText: null,
    oldText: null,
    tone: category.posOnly ? 'missingNeutral' : 'missing',
  };

  if (hasSizes(item)) {
    const sizes = activeSizes(item);
    const prices = sizes.map((size) =>
      Number(size.discountedCost ?? size.cost)
    );
    if (!prices.length || prices.every((price) => price === 0)) return missing;
    const low = Math.min(...prices);
    const high = Math.max(...prices);
    return {
      text: money(low),
      highText: low === high ? null : money(high),
      oldText: null,
      tone: 'normal',
    };
  }

  const price = Number(item.price);
  if (price === 0) return missing;
  if (item.discountedPrice != null)
    return {
      text: money(Number(item.discountedPrice)),
      highText: null,
      oldText: money(price),
      tone: 'normal',
    };
  return { text: money(price), highText: null, oldText: null, tone: 'normal' };
}

function buildRowView(
  item: IItem,
  category: ICategory,
  ctx: ICatalogRowContext
): ICatalogRowView {
  return Object.freeze({
    id: item.id,
    categoryId: item.categoryId,
    name: item.name,
    description: item.description?.trim() || null,
    picture: item.picture || null,
    price: Object.freeze(priceView(item, category, ctx)),
    sizesCount: sortedSizes(item).length,
    groupsCount: (item.itemOptions ?? []).filter(
      (link) => link.active && link.option && !link.option.main
    ).length,
    available: isAvailableHere(item, ctx.branchId),
    status: availabilityStatus(item, ctx.branchId),
    hasHours: !!item.hourRanges?.length || !!category.hourRanges?.length,
  });
}

const rowViewCache = new WeakMap<
  IItem,
  { key: string; view: ICatalogRowView }
>();

/**
 * The row's frozen view, rebuilt only when the item object, the context or
 * the category fields it shows (POS only, has hours) changed.
 */
export function rowView(
  item: IItem,
  category: ICategory,
  ctx: ICatalogRowContext
): ICatalogRowView {
  const key = [
    ctx.branchId,
    ctx.currency,
    ctx.country,
    category.posOnly ? 1 : 0,
    category.hourRanges?.length ? 1 : 0,
  ].join('|');
  const cached = rowViewCache.get(item);
  if (cached && cached.key === key) return cached.view;
  const view = buildRowView(item, category, ctx);
  rowViewCache.set(item, { key, view });
  return view;
}

//#endregion
